/**
 * Created: 2026-06-14
 * Initial commit: Extract computeDisplayHours from SlotsPage useMemo
 *
 * Computes the contiguous hour range to display in the slot grid.
 */

import type { Weekday } from '../../gql/graphql'
import { dateToWeekday } from '../../lib/dateUtils'

/**
 * Compute the contiguous display hour range for the slot grid.
 *
 * Takes the union of:
 *  - hours derived from actual slot start times (catches historical bookings
 *    outside today's template range, e.g. slots booked when courts had longer
 *    hours due to a seasonal schedule change)
 *  - hours from slot templates matching the selected date's weekday (ensures
 *    the full operating window is shown even for past dates with no OPEN slots,
 *    and handles multi-sport sites where courts have different opening hours)
 *
 * Returns a contiguous array [hourStart, …, hourEnd]. Returns [] when both
 * inputs are empty.
 */
export function computeDisplayHours(
  slotStartTimes: string[],
  templates: { weekday: Weekday; hour: number }[],
  date: Date,
): number[] {
  let min = Number.POSITIVE_INFINITY
  let max = Number.NEGATIVE_INFINITY

  for (const startTime of slotStartTimes) {
    const chinaHour = (new Date(startTime).getUTCHours() + 8) % 24
    min = Math.min(min, chinaHour)
    max = Math.max(max, chinaHour)
  }

  const weekdayEnum = dateToWeekday(date)
  for (const t of templates) {
    if (t.weekday === weekdayEnum) {
      min = Math.min(min, t.hour)
      max = Math.max(max, t.hour)
    }
  }

  if (min === Number.POSITIVE_INFINITY) return []
  return Array.from({ length: max - min + 1 }, (_, i) => min + i)
}
